import React from "react";
import { useNavigate } from "react-router-dom";
import { useActivityLogger } from "./hooks/useActivity";

const NotFound = () => {
  const navigate = useNavigate();
  const { logUserActivity, loading } = useActivityLogger();

  React.useEffect(() => {
    if (!loading) {
      logUserActivity(
        "navigation",
        "page_not_found",
        "404_page",
        `User accessed non-existent page: ${window.location.pathname}`
      );
    }
  }, [logUserActivity, loading]);

  const handleGoHome = () => {
    logUserActivity(
      "navigation",
      "return_home_from_404",
      "404_page",
      "User clicked return home from 404 page"
    );
    navigate("/");
  };

  const handleGoBack = () => {
    logUserActivity(
      "navigation",
      "go_back_from_404",
      "404_page",
      "User clicked go back from 404 page"
    );
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="text-center max-w-md mx-auto">
        {/* 404 Illustration */}
        <div className="mb-8">
          <img
            src="/404-illustration.png"
            alt="404 Not Found"
            className="w-64 h-64 mx-auto object-contain"
            onError={(e) => {
              // Fallback to text if image not found
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <div
            className="text-8xl font-bold text-blue-100 hidden"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            404
          </div>
        </div>

        {/* Error Message */}
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Halaman Tidak Ditemukan
        </h1>

        <p className="text-gray-600 mb-8 leading-relaxed">
          Maaf, halaman yang Anda cari tidak dapat ditemukan. Mungkin halaman
          telah dipindahkan atau URL yang Anda masukkan salah.
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleGoHome}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Kembali ke Beranda
          </button>

          <button
            onClick={handleGoBack}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Kembali ke Halaman Sebelumnya
          </button>
        </div>

        {/* Additional Links */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            Atau kunjungi halaman lain:
          </p>

          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => {
                logUserActivity(
                  "navigation",
                  "goto_forum_from_404",
                  "404_page",
                  "User navigated to forum from 404"
                );
                navigate("/forum");
              }}
              className="text-blue-500 hover:text-blue-600 text-sm underline"
            >
              Forum
            </button>

            <span className="text-gray-300">•</span>

            <button
              onClick={() => {
                logUserActivity(
                  "navigation",
                  "goto_articles_from_404",
                  "404_page",
                  "User navigated to articles from 404"
                );
                navigate("/articles");
              }}
              className="text-blue-500 hover:text-blue-600 text-sm underline"
            >
              Artikel
            </button>

            <span className="text-gray-300">•</span>

            <button
              onClick={() => {
                logUserActivity(
                  "navigation",
                  "goto_videos_from_404",
                  "404_page",
                  "User navigated to videos from 404"
                );
                navigate("/videos");
              }}
              className="text-blue-500 hover:text-blue-600 text-sm underline"
            >
              Video
            </button>
          </div>
        </div>

        {/* Current Path Info (untuk debugging, bisa dihapus di production) */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">
            Path:{" "}
            <code className="bg-gray-200 px-1 rounded">
              {window.location.pathname}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
